import { parseLetter, variablesIn, type Block, type Run } from "./markup.js";

/**
 * "list": one item per line of the value. Used as the only thing in a bullet (`- {{offer.responsibilities}}`),
 * it prints one bullet per item — the role's responsibilities in an offer letter.
 */
export type VariableType = "text" | "date" | "money" | "email" | "list";

export interface VariableSpec {
  key: string;
  label: string;
  type: VariableType;
  required: boolean;
  example?: string;
  help?: string;
}

export interface SignatureBlockSpec {
  role: "company" | "signer";
  /** Line above the signature, e.g. "For Golden Sparrow LLC" or "Accepted by". */
  label: string;
  /** Variable holding the printed name. */
  nameVar: string;
  /** Variable holding a title under the name (company signer). */
  titleVar?: string;
}

export interface Letterhead {
  name: string;
  /** Short lines on the right (place, website). */
  lines: string[];
  /** Asset name of the logo in the account's files. */
  logo?: string;
}

/** A letter template version. Frozen once any envelope has used it. */
export interface LetterTemplate {
  key: string;
  version: number;
  title: string;
  body: string;
  variables: VariableSpec[];
  signatures: SignatureBlockSpec[];
  letterhead: Letterhead;
}

export interface Money {
  amount: number;
  currency: string;
  per?: "month" | "year" | "week" | "hour";
}
export type VariableValue = string | number | Money;
export type LetterData = Record<string, VariableValue | undefined>;

/** Always available without being declared. */
export const BUILT_IN_VARIABLES: VariableSpec[] = [
  { key: "signer.name", label: "Signer's name", type: "text", required: true },
  { key: "signer.firstName", label: "Signer's first name", type: "text", required: true },
  { key: "signer.email", label: "Signer's email", type: "email", required: true },
  { key: "company.name", label: "Company name", type: "text", required: true },
  { key: "company.signerName", label: "Company signer's name", type: "text", required: true },
  { key: "company.signerTitle", label: "Company signer's title", type: "text", required: false },
  { key: "today", label: "Today's date", type: "date", required: true },
];

const LOCALE_FOR: Record<string, string> = { INR: "en-IN", USD: "en-US", CAD: "en-CA", GBP: "en-GB", EUR: "en-IE", AUD: "en-AU" };
const PER: Record<string, string> = { month: "a month", year: "a year", week: "a week", hour: "an hour" };

export function formatMoney(m: Money): string {
  const fmt = new Intl.NumberFormat(LOCALE_FOR[m.currency] ?? "en-US", {
    style: "currency",
    currency: m.currency,
    maximumFractionDigits: Number.isInteger(m.amount) ? 0 : 2,
  });
  return fmt.format(m.amount) + (m.per ? ` ${PER[m.per]}` : "");
}

/** "2026-11-03" → "November 3, 2026" (the date as written, no time zone shift). */
export function formatLongDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return new Intl.DateTimeFormat("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" }).format(d);
}

/** A list value's items: one per non-empty line, trimmed. */
export function listItems(v: VariableValue | undefined): string[] {
  return typeof v === "string" ? v.split("\n").map((x) => x.trim()).filter(Boolean) : [];
}

export function formatValue(spec: VariableSpec | undefined, v: VariableValue): string {
  if (typeof v === "object") return formatMoney(v);
  if (spec?.type === "list") return listItems(v).join("; ");
  if (spec?.type === "date" && typeof v === "string") return formatLongDate(v);
  return String(v);
}

export interface TemplateProblem {
  kind: "unknownVariable" | "duplicateVariable" | "missingSignatureVariable" | "emptyBody" | "listOutsideBullet";
  detail: string;
}

/** Checks a template before it can be saved as a version. */
export function checkTemplate(t: LetterTemplate): TemplateProblem[] {
  const problems: TemplateProblem[] = [];
  if (!t.body.trim()) problems.push({ kind: "emptyBody", detail: "The letter has no text." });
  const declared = new Map<string, VariableSpec>();
  for (const v of [...BUILT_IN_VARIABLES, ...t.variables]) {
    if (declared.has(v.key) && t.variables.includes(v)) problems.push({ kind: "duplicateVariable", detail: v.key });
    declared.set(v.key, v);
  }
  const blocks = parseLetter(t.body);
  for (const key of variablesIn(blocks)) {
    if (!declared.has(key)) problems.push({ kind: "unknownVariable", detail: key });
  }
  // A list prints as bullets, so it must be a bullet of its own — never inside a sentence.
  const lists = new Set([...declared.values()].filter((v) => v.type === "list").map((v) => v.key));
  for (const b of blocks) {
    const items = b.kind === "bullets" || b.kind === "numbered" ? b.items : [b.runs];
    for (const runs of items) {
      for (const r of runs) {
        if (r.kind !== "var" || !lists.has(r.key)) continue;
        if ((b.kind !== "bullets" && b.kind !== "numbered") || runs.length !== 1) problems.push({ kind: "listOutsideBullet", detail: r.key });
      }
    }
  }
  for (const s of t.signatures) {
    for (const key of [s.nameVar, s.titleVar].filter((x): x is string => !!x)) {
      if (!declared.has(key)) problems.push({ kind: "missingSignatureVariable", detail: key });
    }
  }
  return problems;
}

export type MergedRun =
  | { kind: "text"; text: string; bold?: boolean; italic?: boolean; /** filled in from a variable */ merged?: string }
  | { kind: "break" };
export type MergedBlock =
  | { kind: "heading"; level: 1 | 2; runs: MergedRun[] }
  | { kind: "paragraph"; runs: MergedRun[] }
  | { kind: "bullets"; items: MergedRun[][] }
  | { kind: "numbered"; start: number; items: MergedRun[][] };

export interface Merged {
  blocks: MergedBlock[];
  /** Required variables with no value — the letter can't be sent until they're filled. */
  missing: string[];
  /** Every variable's printed value, for the signature blocks and previews. */
  printed: Record<string, string>;
}

export function mergeLetter(t: LetterTemplate, data: LetterData): Merged {
  const specs = new Map([...BUILT_IN_VARIABLES, ...t.variables].map((v) => [v.key, v]));
  const blocks = parseLetter(t.body);
  const used = new Set([...variablesIn(blocks), ...t.signatures.flatMap((s) => [s.nameVar, s.titleVar ?? ""]).filter(Boolean)]);
  const printed: Record<string, string> = {};
  const missing: string[] = [];
  for (const key of used) {
    const v = data[key];
    const spec = specs.get(key);
    if (v === undefined || v === "" || (spec?.type === "list" && listItems(v).length === 0)) {
      if (spec?.required !== false) missing.push(key);
      continue;
    }
    printed[key] = formatValue(spec, v);
  }
  const mergeRuns = (runs: Run[]): MergedRun[] =>
    runs.map((r) => {
      if (r.kind !== "var") return r;
      const style = { ...(r.bold ? { bold: true } : {}), ...(r.italic ? { italic: true } : {}) };
      return { kind: "text", text: printed[r.key] ?? `{{${r.key}}}`, merged: r.key, ...style };
    });
  // A bullet that is only a list variable becomes one bullet per item.
  const expand = (item: Run[]): MergedRun[][] => {
    const only = item.length === 1 && item[0]!.kind === "var" ? item[0]! : null;
    if (only && only.kind === "var" && specs.get(only.key)?.type === "list" && printed[only.key] !== undefined) {
      const style = { ...(only.bold ? { bold: true } : {}), ...(only.italic ? { italic: true } : {}) };
      return listItems(data[only.key]).map((text) => [{ kind: "text", text, merged: only.key, ...style }]);
    }
    return [mergeRuns(item)];
  };
  const out: MergedBlock[] = blocks.map((b) => {
    if (b.kind === "bullets" || b.kind === "numbered") return { ...b, items: b.items.flatMap(expand) };
    return { ...b, runs: mergeRuns(b.runs) };
  });
  return { blocks: out, missing: missing.sort(), printed };
}
